import { BuilderSection } from "@/components/studio/builder-section";
import {
  ContentSearchDrawer,
  type CompatibilityLevel,
} from "@/components/studio/content-search-panel";
import { FileUpload } from "@/components/studio/file-upload";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import {
  CONTENT_JOURNEY,
  ContentJourneyService,
  StudioContentSearchResult,
  formatDurationSeconds,
} from "@/lib/api/journey.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type { Category, Coach, TagGroup, TagOption } from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
  getLanguageLabel,
} from "@/lib/studio-constants";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Globe,
  GripVertical,
  Heart,
  Info,
  Map,
  Music,
  Plus,
  Save,
  Search,
  SendHorizontal,
  StickyNote,
  Trash2,
  User,
  Video,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

const journeyConfig = CONTENT_TYPE_CONFIG.journey;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface ContentBlock {
  id: string;
  order: number;
  contentId: string;
  contentType: "audio" | "video" | "blog" | "workbook";
  contentTitle: string | null;
  isRequired: boolean;
  estimatedMinutes: number;
  durationSeconds: number | null;
}

interface DayStructure {
  id: string;
  dayNumber: number;
  title: string;
  description: string;
  completionType: string;
  completionPercentage: number | null;
  contentBlocks: ContentBlock[];
}

const contentTypeIcons: Record<string, React.ElementType> = {
  audio: Music,
  video: Video,
  blog: FileText,
  workbook: BookOpen,
};

function createEmptyDay(dayNumber: number): DayStructure {
  return {
    id: crypto.randomUUID(),
    dayNumber,
    title: "",
    description: "",
    completionType: "all_required",
    completionPercentage: null,
    contentBlocks: [],
  };
}

function getAllContentIds(days: DayStructure[]): string[] {
  return days.flatMap((d) => d.contentBlocks.map((b) => b.contentId));
}

function SortableDayWrapper({
  id,
  children,
}: {
  id: string;
  children: (props: {
    attributes: Record<string, any>;
    listeners: Record<string, any> | undefined;
    setNodeRef: (node: HTMLElement | null) => void;
    isDragging: boolean;
    style: React.CSSProperties;
  }) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    position: "relative",
  };
  return (
    <>{children({ attributes, listeners, setNodeRef, isDragging, style })}</>
  );
}

interface ValidationIssue {
  type: "error" | "warning";
  message: string;
}

function validateJourney(
  title: string,
  tagOptionIds: string[],
  selectedLanguages: string[],
  dayStructure: DayStructure[],
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!title.trim())
    issues.push({ type: "error", message: "Title is required" });
  if (selectedLanguages.length === 0)
    issues.push({
      type: "error",
      message: "At least 1 language is required",
    });
  if (tagOptionIds.length === 0)
    issues.push({
      type: "error",
      message: "At least 1 personalization tag is required",
    });
  if (dayStructure.length === 0)
    issues.push({ type: "error", message: "At least 1 day is required" });
  const hasAnyDayWithContent = dayStructure.some(
    (d) => d.contentBlocks.length > 0,
  );
  if (dayStructure.length > 0 && !hasAnyDayWithContent) {
    issues.push({
      type: "error",
      message: "At least 1 day must have content blocks",
    });
  }
  dayStructure.forEach((day) => {
    if (!day.title.trim())
      issues.push({
        type: "warning",
        message: `Day ${day.dayNumber} needs a title`,
      });
    if (day.contentBlocks.length === 0)
      issues.push({
        type: "warning",
        message: `Day ${day.dayNumber} has no content blocks`,
      });
    if (day.contentBlocks.length > 6)
      issues.push({
        type: "warning",
        message: `Day ${day.dayNumber} may be too heavy (${day.contentBlocks.length} blocks)`,
      });
  });
  return issues;
}

export default function JourneyCreatePage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [totalDays, setTotalDays] = useState<number>(1);
  const [estimatedDailyMinutes, setEstimatedDailyMinutes] =
    useState<number>(15);
  const [difficultyLevel, setDifficultyLevel] = useState("beginner");
  const [progressionType, setProgressionType] = useState("sequential");
  const [completionType, setCompletionType] = useState("all_required");
  const [completionPercentage, setCompletionPercentage] = useState<
    number | null
  >(null);
  const [coreIntention, setCoreIntention] = useState("");
  const [emotionalOutcomePromise, setEmotionalOutcomePromise] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [tagOptionIds, setTagOptionIds] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(["en"]);
  const [coverImage, setCoverImage] = useState("");
  const [authorCoachId, setAuthorCoachId] = useState<string | null>(null);

  const [dayStructure, setDayStructure] = useState<DayStructure[]>([]);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const [searchDrawerDayId, setSearchDrawerDayId] = useState<string | null>(
    null,
  );
  const [compatWarning, setCompatWarning] = useState<{
    dayId: string;
    result: StudioContentSearchResult;
    level: CompatibilityLevel;
  } | null>(null);

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  useEffect(() => {
    if (isCoachOnly && selfCoachId && !authorCoachId) {
      setAuthorCoachId(selfCoachId);
    }
  }, [isCoachOnly, selfCoachId, authorCoachId]);

  const allContentIds = getAllContentIds(dayStructure);
  const searchDrawerDay = dayStructure.find((d) => d.id === searchDrawerDayId);

  const createMutation = useMutation({
    mutationFn: async (submitForApproval: boolean) => {
      const payload = {
        title,
        description: description || null,
        totalDays: dayStructure.length || totalDays,
        estimatedDailyMinutes,
        difficultyLevel,
        progressionType,
        coreIntention: coreIntention || null,
        emotionalOutcomePromise: emotionalOutcomePromise || null,
        internalNotes: internalNotes || null,
        categoryId,
        tagOptionIds,
        authorCoachId,
        primaryLanguage: selectedLanguages[0] || "en",
        selectedLanguages,
        thumbnailUrl: coverImage || null,
        dayStructure: dayStructure.map((day) => ({
          id: day.id,
          dayNumber: day.dayNumber,
          title: day.title || `Day ${day.dayNumber}`,
          theme: null,
          description: day.description || null,
          completionType: day.completionType,
          completionPercentage: day.completionPercentage,
          contentBlocks: day.contentBlocks.map((block) => ({
            id: block.id,
            order: block.order,
            contentId: block.contentId,
            contentType: block.contentType,
            contentTitle: block.contentTitle,
            isRequired: block.isRequired,
            estimatedMinutes: block.estimatedMinutes,
          })),
          ritualIds: [],
        })),
      };

      const data = await ContentJourneyService.create(payload);

      if (submitForApproval) {
        try {
          await ContentJourneyService.submitForReview(data.id);
        } catch (submitErr: any) {
          const msg =
            submitErr?.message?.replace(/^\d+:\s*/, "") || "Submission failed";
          let parsed = msg;
          try {
            parsed = JSON.parse(msg).error || msg;
          } catch {}
          throw new Error(`Draft saved but could not submit: ${parsed}`);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTENT_JOURNEY] });
      toast({ title: "Journey created" });
      setLocation("/studio/journeys");
    },
    onError: (err: Error) => {
      queryClient.invalidateQueries({ queryKey: [CONTENT_JOURNEY] });
      toast({
        title: err.message || "Failed to create journey",
        variant: "destructive",
      });
    },
  });

  const addDay = () => {
    let newId = "";
    setDayStructure((prev) => {
      const newDay = createEmptyDay(prev.length + 1);
      newId = newDay.id;
      return [...prev, newDay];
    });
    if (newId) setExpandedDays((prev) => new Set(prev).add(newId));
  };

  const removeDay = (dayId: string) => {
    setDayStructure((prev) =>
      prev
        .filter((d) => d.id !== dayId)
        .map((d, i) => ({ ...d, dayNumber: i + 1 })),
    );
    setExpandedDays((prev) => {
      const next = new Set(prev);
      next.delete(dayId);
      return next;
    });
  };

  const moveDayUp = (index: number) => {
    setDayStructure((prev) => {
      if (index === 0 || index >= prev.length) return prev;
      const updated = [...prev];
      [updated[index - 1], updated[index]] = [
        updated[index],
        updated[index - 1],
      ];
      return updated.map((d, i) => ({ ...d, dayNumber: i + 1 }));
    });
  };

  const moveDayDown = (index: number) => {
    setDayStructure((prev) => {
      if (index < 0 || index >= prev.length - 1) return prev;
      const updated = [...prev];
      [updated[index], updated[index + 1]] = [
        updated[index + 1],
        updated[index],
      ];
      return updated.map((d, i) => ({ ...d, dayNumber: i + 1 }));
    });
  };

  const updateDay = (
    dayId: string,
    field: keyof DayStructure,
    value: unknown,
  ) => {
    setDayStructure((prev) =>
      prev.map((d) => (d.id === dayId ? { ...d, [field]: value } : d)),
    );
  };

  const addContentToDay = (
    dayId: string,
    result: StudioContentSearchResult,
  ) => {
    const existingIds = new Set(getAllContentIds(dayStructure));
    if (existingIds.has(result.id)) {
      toast({
        title: "Already in this journey",
        description: `"${result.title}" is used on another day. Use the duplicate warning to add anyway.`,
        variant: "destructive",
      });
      return;
    }
    setDayStructure((prev) =>
      prev.map((d) => {
        if (d.id !== dayId) return d;
        const newBlock: ContentBlock = {
          id: crypto.randomUUID(),
          order: d.contentBlocks.length + 1,
          contentId: result.id,
          contentType: result.contentType,
          contentTitle: result.title,
          isRequired: true,
          estimatedMinutes: 5,
          durationSeconds: result.durationSeconds ?? null,
        };
        return { ...d, contentBlocks: [...d.contentBlocks, newBlock] };
      }),
    );
  };

  const removeBlock = (dayId: string, blockId: string) => {
    setDayStructure((prev) =>
      prev.map((d) => {
        if (d.id !== dayId) return d;
        const blocks = d.contentBlocks
          .filter((b) => b.id !== blockId)
          .map((b, i) => ({ ...b, order: i + 1 }));
        return { ...d, contentBlocks: blocks };
      }),
    );
  };

  const moveBlockUp = (dayId: string, index: number) => {
    if (index === 0) return;
    setDayStructure((prev) =>
      prev.map((d) => {
        if (d.id !== dayId) return d;
        const blocks = [...d.contentBlocks];
        [blocks[index - 1], blocks[index]] = [blocks[index], blocks[index - 1]];
        return {
          ...d,
          contentBlocks: blocks.map((b, i) => ({ ...b, order: i + 1 })),
        };
      }),
    );
  };

  const moveBlockDown = (dayId: string, index: number) => {
    setDayStructure((prev) =>
      prev.map((d) => {
        if (d.id !== dayId) return d;
        if (index >= d.contentBlocks.length - 1) return d;
        const blocks = [...d.contentBlocks];
        [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
        return {
          ...d,
          contentBlocks: blocks.map((b, i) => ({ ...b, order: i + 1 })),
        };
      }),
    );
  };

  const toggleBlockRequired = (dayId: string, blockId: string) => {
    setDayStructure((prev) =>
      prev.map((d) => {
        if (d.id !== dayId) return d;
        return {
          ...d,
          contentBlocks: d.contentBlocks.map((b) =>
            b.id === blockId ? { ...b, isRequired: !b.isRequired } : b,
          ),
        };
      }),
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDayDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = dayStructure.findIndex((d) => d.id === active.id);
    const to = dayStructure.findIndex((d) => d.id === over.id);
    if (from === -1 || to === -1) return;
    const updated = [...dayStructure];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setDayStructure(updated.map((d, i) => ({ ...d, dayNumber: i + 1 })));
  };

  const toggleDayExpanded = (dayId: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayId)) next.delete(dayId);
      else next.add(dayId);
      return next;
    });
  };

  const toggleTag = (tagId: string) => {
    setTagOptionIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const validationIssues = validateJourney(
    title,
    tagOptionIds,
    selectedLanguages,
    dayStructure,
  );
  const hasErrors = validationIssues.some((i) => i.type === "error");
  // Blank form shouldn't open in red; reveal issues after the first Submit.
  const [showValidation, setShowValidation] = useState(false);

  const handleSubmit = (submitForApproval: boolean) => {
    if (submitForApproval) setShowValidation(true);
    if (hasErrors && submitForApproval) {
      const errorMessages = validationIssues
        .filter((i) => i.type === "error")
        .map((i) => i.message);
      toast({
        title: "Cannot submit",
        description: errorMessages.join(". "),
        variant: "destructive",
      });
      return;
    }
    createMutation.mutate(submitForApproval);
  };

  return (
    <div className="flex flex-col h-full" data-testid="journey-create-page">
      <div className="flex items-center justify-between gap-4 p-4 border-b flex-wrap">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/studio/journeys")}
            data-testid="button-back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div
            className="p-2 rounded-md"
            style={{ background: "linear-gradient(135deg, #8b5cf6, #6d28d9)" }}
          >
            <Map className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold" data-testid="text-page-title">
              New Journey
            </h1>
            <p className="text-xs text-muted-foreground">
              Build a multi-day program
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleSubmit(false)}
            disabled={
              !title ||
              selectedLanguages.length === 0 ||
              createMutation.isPending
            }
            data-testid="button-save-draft"
          >
            <Save className="h-4 w-4 mr-1" />
            Save Draft
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            disabled={
              !title ||
              selectedLanguages.length === 0 ||
              createMutation.isPending
            }
            data-testid="button-submit"
          >
            <SendHorizontal className="h-4 w-4 mr-1" />
            Submit
          </Button>
        </div>
      </div>

      {showValidation && validationIssues.length > 0 && (
        <div className="px-4 py-2 border-b text-xs space-y-1">
          {validationIssues.map((issue, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 ${
                issue.type === "error"
                  ? "text-destructive"
                  : "text-amber-700 dark:text-amber-400"
              }`}
              data-testid={`validation-${issue.type}-${i}`}
            >
              <AlertTriangle className="h-3 w-3 shrink-0" />
              <span>{issue.message}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <ScrollArea className="w-80 border-r shrink-0">
          <div className="p-4 space-y-6">
            <BuilderSection
              title="Basic Info"
              description="Title, description, and cover image"
              icon={Info}
            >
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Journey title..."
                  data-testid="input-journey-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this journey..."
                  rows={3}
                  data-testid="input-journey-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Cover Image</Label>
                <FileUpload
                  value={coverImage}
                  onChange={setCoverImage}
                  type="image"
                  label="Upload cover image"
                />
              </div>
            </BuilderSection>

            <BuilderSection
              title="Languages"
              description="All content must be available in every selected language"
              icon={Globe}
              badge={
                selectedLanguages.length > 0
                  ? `${selectedLanguages.length} selected`
                  : undefined
              }
            >
              {selectedLanguages.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedLanguages.map((code) => (
                    <Badge
                      key={code}
                      variant="secondary"
                      className="cursor-pointer gap-1 pr-1"
                      onClick={() =>
                        setSelectedLanguages((prev) =>
                          prev.filter((c) => c !== code),
                        )
                      }
                      data-testid={`badge-language-${code}`}
                    >
                      {getLanguageLabel(code)}
                      <X className="h-3 w-3" />
                    </Badge>
                  ))}
                </div>
              )}
              <Select
                value=""
                onValueChange={(code) => {
                  if (!selectedLanguages.includes(code)) {
                    setSelectedLanguages((prev) => [...prev, code]);
                  }
                }}
              >
                <SelectTrigger data-testid="select-journey-language">
                  <SelectValue placeholder="Add language..." />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.filter(
                    (lang) => !selectedLanguages.includes(lang.code),
                  ).map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedLanguages.length === 0 && (
                <p className="text-xs text-destructive">
                  At least one language is required before creating a journey.
                </p>
              )}
            </BuilderSection>

            <IntentTaggingSection
              categories={categories}
              categoryId={categoryId || ""}
              onCategoryChange={(id) => setCategoryId(id || null)}
              tagGroups={tagGroups}
              getGroupOptions={getGroupOptions}
              selectedTagOptionIds={tagOptionIds}
              onToggleTagOption={toggleTag}
            />

            <BuilderSection
              title="Author"
              description="Select the coach attributed as the journey's author"
              icon={User}
            >
              <div className="space-y-2">
                <Label>Author Coach</Label>
                {isCoachOnly ? (
                  <Input
                    value={user?.name ?? ""}
                    disabled
                    data-testid="select-author-coach"
                  />
                ) : (
                  <Select
                    value={authorCoachId || "none"}
                    onValueChange={(val) =>
                      setAuthorCoachId(val === "none" ? null : val)
                    }
                  >
                    <SelectTrigger data-testid="select-author-coach">
                      <SelectValue placeholder="Select author" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No author</SelectItem>
                      {coaches.map((coach) => (
                        <SelectItem key={coach.id} value={coach.id}>
                          {coach.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </BuilderSection>

            <BuilderSection
              title="Emotional Intent"
              description="Core intention and emotional outcome"
              icon={Heart}
            >
              <div className="space-y-2">
                <Label className="text-xs">Core Intention</Label>
                <Textarea
                  value={coreIntention}
                  onChange={(e) => setCoreIntention(e.target.value)}
                  placeholder="What is the core intention?"
                  rows={2}
                  data-testid="input-core-intention"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Emotional Outcome Promise</Label>
                <Textarea
                  value={emotionalOutcomePromise}
                  onChange={(e) => setEmotionalOutcomePromise(e.target.value)}
                  placeholder="What emotional outcome?"
                  rows={2}
                  data-testid="input-emotional-outcome"
                />
              </div>
            </BuilderSection>

            <BuilderSection
              title="Notes"
              description="Internal notes for your team"
              icon={StickyNote}
              defaultOpen={false}
            >
              <Textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Internal notes..."
                rows={2}
                data-testid="input-internal-notes"
              />
            </BuilderSection>
          </div>
        </ScrollArea>

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-4 p-4 border-b flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold">Day Builder</h2>
              <Badge variant="secondary">{dayStructure.length} days</Badge>
            </div>
            <Button size="sm" onClick={addDay} data-testid="button-add-day">
              <Plus className="h-3 w-3 mr-1" />
              Add Day
            </Button>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-3">
              {dayStructure.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="p-4 rounded-full bg-muted mb-4">
                    <Map className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3
                    className="text-sm font-semibold mb-1"
                    data-testid="text-no-days"
                  >
                    No days yet
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4 max-w-sm">
                    Start building your journey by adding days. Each day
                    contains content blocks that guide users through the
                    program.
                  </p>
                  <Button
                    size="sm"
                    onClick={addDay}
                    data-testid="button-add-first-day"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add First Day
                  </Button>
                </div>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDayDragEnd}
              >
                <SortableContext
                  items={dayStructure.map((d) => d.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {dayStructure.map((day, dayIndex) => {
                      const isExpanded = expandedDays.has(day.id);
                      const totalSeconds = day.contentBlocks.reduce(
                        (sum, b) => sum + (b.durationSeconds ?? 0),
                        0,
                      );
                      return (
                        <SortableDayWrapper key={day.id} id={day.id}>
                          {({
                            setNodeRef,
                            attributes,
                            listeners,
                            isDragging,
                            style,
                          }) => (
                            <div ref={setNodeRef} style={style} {...attributes}>
                              <Card
                                className={`overflow-visible ${isDragging ? "ring-2 ring-primary/30 shadow-lg" : ""}`}
                                data-testid={`day-card-${day.id}`}
                              >
                                <div
                                  className="flex items-center gap-2 p-3 cursor-pointer"
                                  onClick={() => toggleDayExpanded(day.id)}
                                  data-testid={`day-header-${day.id}`}
                                >
                                  <div
                                    {...listeners}
                                    className="shrink-0 cursor-grab active:cursor-grabbing"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                  <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                                    <Badge
                                      variant="outline"
                                      className="shrink-0"
                                    >
                                      Day {day.dayNumber}
                                    </Badge>
                                    <span className="text-sm font-medium truncate">
                                      {day.title || "Untitled"}
                                    </span>
                                    <Badge
                                      variant="secondary"
                                      className="shrink-0"
                                    >
                                      {day.contentBlocks.length} blocks
                                    </Badge>
                                    {totalSeconds > 0 && (
                                      <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                        <Clock className="h-3 w-3" />
                                        {formatDurationSeconds(totalSeconds)}
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    className="flex items-center gap-1 shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => moveDayUp(dayIndex)}
                                      disabled={dayIndex === 0}
                                    >
                                      <ChevronUp className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => moveDayDown(dayIndex)}
                                      disabled={
                                        dayIndex === dayStructure.length - 1
                                      }
                                    >
                                      <ChevronDown className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => removeDay(day.id)}
                                    >
                                      <Trash2 className="h-3 w-3 text-destructive" />
                                    </Button>
                                  </div>
                                </div>

                                {isExpanded && (
                                  <div className="px-4 pb-4 space-y-4 border-t pt-4">
                                    <div className="space-y-1">
                                      <Label className="text-xs">
                                        Day Title
                                      </Label>
                                      <Input
                                        value={day.title}
                                        onChange={(e) =>
                                          updateDay(
                                            day.id,
                                            "title",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Day title..."
                                        data-testid="input-day-title"
                                      />
                                    </div>

                                    <div className="space-y-1">
                                      <Label className="text-xs">
                                        Day Description
                                      </Label>
                                      <Textarea
                                        value={day.description}
                                        onChange={(e) =>
                                          updateDay(
                                            day.id,
                                            "description",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Describe this day..."
                                        rows={2}
                                        data-testid="input-day-description"
                                      />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                      <div className="space-y-1">
                                        <Label className="text-xs">
                                          Day Completion
                                        </Label>
                                        <Select
                                          value={day.completionType}
                                          onValueChange={(val) =>
                                            updateDay(
                                              day.id,
                                              "completionType",
                                              val,
                                            )
                                          }
                                        >
                                          <SelectTrigger data-testid="select-day-completion">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="all_required">
                                              All Required
                                            </SelectItem>
                                            <SelectItem value="percentage">
                                              Percentage
                                            </SelectItem>
                                            <SelectItem value="any_one">
                                              Any One
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      {day.completionType === "percentage" && (
                                        <div className="space-y-1">
                                          <Label className="text-xs">
                                            Required %
                                          </Label>
                                          <Input
                                            type="number"
                                            min={1}
                                            max={100}
                                            value={
                                              day.completionPercentage ?? ""
                                            }
                                            onChange={(e) =>
                                              updateDay(
                                                day.id,
                                                "completionPercentage",
                                                e.target.value
                                                  ? parseInt(e.target.value)
                                                  : null,
                                              )
                                            }
                                            placeholder="80"
                                            data-testid="input-day-completion-pct"
                                          />
                                        </div>
                                      )}
                                    </div>

                                    <div className="space-y-2 pt-1">
                                      <div className="flex items-center justify-between gap-2">
                                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                          Content Blocks
                                        </Label>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() =>
                                            setSearchDrawerDayId(day.id)
                                          }
                                          disabled={
                                            selectedLanguages.length === 0
                                          }
                                          data-testid={`button-add-content-${day.id}`}
                                        >
                                          <Search className="h-3 w-3 mr-1" />
                                          Add Content
                                        </Button>
                                      </div>

                                      {selectedLanguages.length === 0 &&
                                        day.contentBlocks.length === 0 && (
                                          <div className="p-4 border border-dashed rounded-md text-center">
                                            <Globe className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
                                            <p className="text-sm text-muted-foreground">
                                              Select at least one language first
                                              to add content
                                            </p>
                                          </div>
                                        )}

                                      {selectedLanguages.length > 0 &&
                                        day.contentBlocks.length === 0 && (
                                          <div
                                            className="p-6 border border-dashed rounded-md text-center cursor-pointer hover-elevate"
                                            onClick={() =>
                                              setSearchDrawerDayId(day.id)
                                            }
                                            data-testid={`empty-blocks-${day.id}`}
                                          >
                                            <Plus className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
                                            <p className="text-sm text-muted-foreground">
                                              Add content to this day
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                              Audio, video, Blog, Workbook
                                            </p>
                                          </div>
                                        )}

                                      {day.contentBlocks.map(
                                        (block, blockIdx) => {
                                          const Icon =
                                            contentTypeIcons[
                                              block.contentType
                                            ] || FileText;
                                          const isDuplicate =
                                            allContentIds.filter(
                                              (cid) => cid === block.contentId,
                                            ).length > 1;
                                          return (
                                            <div
                                              key={block.id}
                                              className="flex items-center gap-2 p-2.5 rounded-md border text-sm"
                                              data-testid={`block-${block.id}`}
                                            >
                                              <div className="flex flex-col gap-0.5 shrink-0">
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-4 w-4"
                                                  onClick={() =>
                                                    moveBlockUp(
                                                      day.id,
                                                      blockIdx,
                                                    )
                                                  }
                                                  disabled={blockIdx === 0}
                                                >
                                                  <ChevronUp className="h-3 w-3" />
                                                </Button>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-4 w-4"
                                                  onClick={() =>
                                                    moveBlockDown(
                                                      day.id,
                                                      blockIdx,
                                                    )
                                                  }
                                                  disabled={
                                                    blockIdx ===
                                                    day.contentBlocks.length - 1
                                                  }
                                                >
                                                  <ChevronDown className="h-3 w-3" />
                                                </Button>
                                              </div>
                                              <div className="p-1.5 rounded-md bg-muted shrink-0">
                                                <Icon className="h-4 w-4 text-muted-foreground" />
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate text-sm">
                                                  {block.contentTitle ||
                                                    block.contentId}
                                                </p>
                                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                  <Badge
                                                    variant="outline"
                                                    className="text-xs capitalize"
                                                  >
                                                    {block.contentType}
                                                  </Badge>
                                                  {block.durationSeconds != null &&
                                                    block.durationSeconds > 0 && (
                                                      <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                                        <Clock className="h-3 w-3" />
                                                        {formatDurationSeconds(
                                                          block.durationSeconds,
                                                        )}
                                                      </span>
                                                    )}
                                                  {isDuplicate && (
                                                    <Badge
                                                      variant="secondary"
                                                      className="text-xs"
                                                    >
                                                      <AlertTriangle className="h-3 w-3 mr-0.5" />
                                                      Duplicate
                                                    </Badge>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-1 shrink-0">
                                                <div className="flex items-center gap-1">
                                                  <Switch
                                                    checked={block.isRequired}
                                                    onCheckedChange={() =>
                                                      toggleBlockRequired(
                                                        day.id,
                                                        block.id,
                                                      )
                                                    }
                                                    className="h-4 w-7"
                                                    data-testid={`switch-required-${block.id}`}
                                                  />
                                                  <span className="text-xs text-muted-foreground">
                                                    Req
                                                  </span>
                                                </div>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  onClick={() =>
                                                    removeBlock(
                                                      day.id,
                                                      block.id,
                                                    )
                                                  }
                                                  data-testid={`button-remove-block-${block.id}`}
                                                >
                                                  <X className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Card>
                            </div>
                          )}
                        </SortableDayWrapper>
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </ScrollArea>
        </div>
      </div>

      {searchDrawerDay && (
        <ContentSearchDrawer
          open={!!searchDrawerDayId}
          onOpenChange={(open) => {
            if (!open) setSearchDrawerDayId(null);
          }}
          dayNumber={searchDrawerDay.dayNumber}
          dayId={searchDrawerDay.id}
          journeyTagOptionIds={tagOptionIds}
          journeyCategoryId={categoryId}
          allContentIds={allContentIds}
          selectedLanguages={selectedLanguages}
          allTagOptions={tagOptions}
          onAddContent={addContentToDay}
          onAddContentWithWarning={(dayId, result, level) => {
            setCompatWarning({ dayId, result, level });
          }}
        />
      )}

      <AlertDialog
        open={!!compatWarning}
        onOpenChange={() => setCompatWarning(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Content Targeting Mismatch
            </AlertDialogTitle>
            <AlertDialogDescription>
              This content's targeting differs from the journey's
              personalization tags. Adding mismatched content may reduce the
              journey's effectiveness for the intended audience.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-add">
              See better matches
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (compatWarning) {
                  addContentToDay(compatWarning.dayId, compatWarning.result);
                }
                setCompatWarning(null);
              }}
              data-testid="button-add-anyway"
            >
              Add anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
